import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  SxProps,
  Theme
} from '@mui/material';
import TableStatusView from './TableStatusView';

interface DataTableProps<Data> {
  data?: Data[];
  columns: ColumnDef<Data>[];
  isHeaderSticky?: boolean;
  isHideHeader?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  isLoading?: boolean;
  containerStyles?: SxProps<Theme>;
}

export default function DataTable<Data>({
  data = [],
  columns,
  isHeaderSticky = true,
  isHideHeader = false,
  sorting,
  onSortingChange,
  isLoading,
  containerStyles
}: DataTableProps<Data>) {
  const theme = useTheme();
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting
    },
    manualPagination: true,
    onSortingChange
  });

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: '1px solid #ECECEC', borderBottom: 0, borderRadius: 2, ...containerStyles }}
    >
      <Table stickyHeader={isHeaderSticky} aria-label='data table' sx={{ tableLayout: 'auto' }}>
        {!isHideHeader && (
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    align='left'
                    sortDirection={header.column.getIsSorted() ? header.column.getIsSorted() : false}
                    sx={{
                      fontWeight: '600',
                      fontSize: '14px',
                      color: theme.palette.secondary.main,
                      backgroundColor: theme.palette.background.paper,
                      textTransform: 'uppercase'
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
        )}
        <TableStatusView
          isLoading={isLoading}
          hasNoRecords={!data.length}
          colCount={table.getHeaderGroups()[0].headers.length}
        />
        <TableBody>
          {table.getRowModel().rows.map((row, index) => (
            <TableRow
              key={row.id}
              sx={{
                borderBottom: index === table.getRowModel().rows.length - 1 ? 'none' : '1px solid #ECECEC'
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  sx={{
                    padding: '18px',
                    fontSize: '16px',
                    color: theme.palette.text.primary
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
